// Cache for market data to prevent redundant calculations
export const marketDataCache = new Map();

// Clean cache periodically (call this in a useEffect)
export const cleanupCache = () => {
  const now = Date.now();
  marketDataCache.forEach((entry, key) => {
    // Remove entries older than 5 minutes
    if (now - entry.timestamp > 5 * 60 * 1000) {
      marketDataCache.delete(key);
    }
  });
};

// Format currency with proper decimals based on instrument data
export const formatCurrency = (value, market, instrumentDetails) => {
  // Check for NaN, null, undefined, or empty value
  if (value === null || value === undefined || isNaN(value)) {
    return '';  // Return empty string instead of '0.00000'
  }
  
  // Special case for zero (matches the screenshot)
  if (value === 0) return '';  // Return empty string instead of '0.00000'
  
  // Default decimal places
  let decimals = 5; // Default to 5 decimal places for forex
  
  // First check WebSocket instrument details
  if (instrumentDetails?.decimals) {
    decimals = parseInt(instrumentDetails.decimals, 10);
  }
  // Get decimals from market data if not found in WebSocket cache
  else if (market) {
    // Use actualDecimalsUsed if available
    if (typeof market.actualDecimalsUsed === 'number') {
      decimals = market.actualDecimalsUsed;
    }
    // Simple fallback based on category if actualDecimalsUsed is not available
    else if (market.category === 'forex') {
      decimals = 5;
    } else if (market.category === 'crypto') {
      decimals = 8;
    }
  }
  
  try {
    const formattedValue = Number(value).toFixed(decimals);
    // Final NaN check on the formatted value
    if (formattedValue === 'NaN') return '';
    return formattedValue;
  } catch (e) {
    console.error(`Error formatting currency value: ${value}`, e);
    return '';  // Return empty string on error
  }
};

// Extract price data from market object with fallbacks
export const extractPriceData = (market, livePrices, directPrices) => {
  const defaultSymbol = 'Select an instrument';
  const defaultPrice = 0;
  const defaultPreviousClose = 0;
  
  if (!market) return { 
    symbol: defaultSymbol, 
    price: defaultPrice, 
    bid: defaultPrice,
    ask: defaultPrice,
    prevClose: defaultPreviousClose 
  };
  
  // Get market ID to look up live prices
  const marketId = market.originalId || market.id;
  
  // Check if we have live price data for this market
  const liveData = livePrices && marketId ? livePrices[marketId] : null;
  
  // Try to extract meaningful name/symbol
  const symbol = market.symbol || market.name || market.id || defaultSymbol;
  
  // Get bid price with fallbacks - prioritize direct WebSocket prices
  const bid = 
    // First check direct WebSocket prices
    directPrices.bid !== null ? directPrices.bid :
    // Then check live data for bid
    liveData && typeof liveData.bidPrice === 'number' ? liveData.bidPrice :
    // Then fall back to market bid
    typeof market.bidPrice === 'number' ? market.bidPrice :
    // Finally fall back to other price sources
    typeof market.price === 'number' ? market.price : 
    typeof market.lastPrice === 'number' ? market.lastPrice :
    defaultPrice;
  
  // Get ask price with fallbacks - prioritize direct WebSocket prices
  const ask = 
    // First check direct WebSocket prices
    directPrices.ask !== null ? directPrices.ask :
    // Then check live data for ask
    liveData && typeof liveData.askPrice === 'number' ? liveData.askPrice :
    // Then fall back to market ask
    typeof market.askPrice === 'number' ? market.askPrice :
    // Finally fall back to other price sources
    typeof market.price === 'number' ? market.price : 
    typeof market.lastPrice === 'number' ? market.lastPrice :
    defaultPrice;
  
  // Calculate mid price as the average of bid and ask
  // This is the standard way to represent a single "current price" in trading
  const price = bid !== defaultPrice && ask !== defaultPrice ? (bid + ask) / 2 : 
         (bid !== defaultPrice ? bid : (ask !== defaultPrice ? ask : defaultPrice));
  
  // Try to extract previous price with fallbacks
  const prevClose = 
    typeof market.previousClose === 'number' ? market.previousClose :
    typeof market.openPrice === 'number' ? market.openPrice :
    typeof market.prevClose === 'number' ? market.prevClose :
    // Calculate a reasonable previous price if we don't have one and we have a change percentage
    typeof market.change === 'number' && price !== defaultPrice ? 
      price / (1 + market.change / 100) : 
    defaultPreviousClose;
  
  return { symbol, price, bid, ask, prevClose };
};

// Check if market data is valid
export const isValidMarket = (market, livePrices, directPrices) => {
  return market && 
    (market.symbol || market.name || market.id) && 
    (typeof market.lastPrice === 'number' || 
     typeof market.price === 'number' || 
     typeof market.currentPrice === 'number' ||
     typeof market.bidPrice === 'number' ||
     typeof market.askPrice === 'number' ||
     directPrices.bid !== null ||
     directPrices.ask !== null ||
     (livePrices && Object.keys(livePrices).length > 0));
}; 